import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Tag, Share2, Twitter, Facebook, Linkedin, MessageCircle, Copy, Check } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import useAnalytics from '../hooks/useAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SinglePost = () => {
  const { slug } = useParams();
  useAnalytics('post', { slug });
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${slug}`);
      setPost(response.data);
      
      // Fetch related posts
      if (response.data.category) {
        const relatedRes = await axios.get(`${API}/posts?category=${response.data.category}&limit=3`);
        setRelatedPosts(relatedRes.data.filter(p => p.id !== response.data.id).slice(0, 2));
      }
    } catch (error) {
      console.error('Failed to fetch post:', error);
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = window.location.href;

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-800"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-stone-50 pt-32 text-center" data-testid="post-not-found">
        <h1 className="font-serif text-4xl text-stone-900 mb-4">Story Not Found</h1>
        <p className="text-stone-600 mb-8">The story you're looking for doesn't exist.</p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-red-800 hover:text-red-900"
        >
          <ArrowLeft size={16} /> Back to Stories
        </Link>
      </div>
    );
  }

  const formattedDate = post.created_at
    ? format(new Date(post.created_at), 'MMMM d, yyyy')
    : '';

  return (
    <div className="min-h-screen bg-stone-50" data-testid="single-post-page">
      <SEO
        title={post.title}
        description={post.excerpt}
        image={post.featured_image}
        url={`/blog/${post.slug}`}
        type="article"
      />
      {/* Hero Image */}
      <section className="relative h-[60vh] md:h-[70vh]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${post.featured_image || 'https://images.unsplash.com/photo-1681018754447-abe57371b5de?crop=entropy&cs=srgb&fm=jpg&q=85'}')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/30 to-transparent"></div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="max-w-4xl mx-auto px-6 md:px-12 pb-12 md:pb-16">
            {post.category && (
              <span className="text-sm tracking-widest uppercase text-amber-500 font-medium mb-4 inline-block">
                {post.category}
              </span>
            )}
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-stone-50 tracking-tight leading-tight" data-testid="post-title">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 mt-6 text-stone-300 text-sm">
              <span className="flex items-center gap-2">
                <User size={16} />
                {post.author_name}
              </span>
              <span className="flex items-center gap-2">
                <Calendar size={16} />
                {formattedDate}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Back Link */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-900 mb-12 transition-colors"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft size={16} /> Back to Stories
          </Link>

          {/* Excerpt */}
          <p className="text-xl md:text-2xl text-stone-700 leading-relaxed font-light mb-12 border-l-4 border-red-800 pl-6">
            {post.excerpt}
          </p>

          {/* Main Content */}
          <div 
            className="blog-content prose prose-stone max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
            data-testid="post-content"
          />

          {/* Video Embed */}
          {post.video_url && (
            <div className="mt-12 aspect-video" data-testid="post-video">
              <iframe
                src={post.video_url.replace('watch?v=', 'embed/')}
                className="w-full h-full rounded-sm"
                allowFullScreen
                title="Video"
              />
            </div>
          )}

          {/* Gallery */}
          {post.gallery && post.gallery.length > 0 && (
            <div className="mt-12" data-testid="post-gallery">
              <h3 className="font-serif text-2xl text-stone-900 mb-6">Photo Gallery</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {post.gallery.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Gallery image ${index + 1}`}
                    loading="lazy"
                    className="w-full rounded-sm"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-stone-200" data-testid="post-tags">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag size={16} className="text-stone-400" />
                {post.tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="bg-stone-100 text-stone-600 hover:bg-stone-200"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="mt-8 pt-8 border-t border-stone-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <Share2 size={18} className="text-stone-400" />
                <span className="text-sm text-stone-500">Share this story</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-[#1DA1F2] hover:text-white transition-all duration-300"
                  title="Share on Twitter"
                  data-testid="share-twitter"
                >
                  <Twitter size={18} />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-[#4267B2] hover:text-white transition-all duration-300"
                  title="Share on Facebook"
                  data-testid="share-facebook"
                >
                  <Facebook size={18} />
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(post.title + ' - ' + shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-[#25D366] hover:text-white transition-all duration-300"
                  title="Share on WhatsApp"
                  data-testid="share-whatsapp"
                >
                  <MessageCircle size={18} />
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-[#0077B5] hover:text-white transition-all duration-300"
                  title="Share on LinkedIn"
                  data-testid="share-linkedin"
                >
                  <Linkedin size={18} />
                </a>
                <button
                  onClick={copyToClipboard}
                  className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-900 hover:text-white transition-all duration-300"
                  title="Copy link"
                  data-testid="share-copy"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 md:py-24 bg-white border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
            <h2 className="text-sm tracking-widest uppercase text-stone-500 font-medium mb-8">
              More Stories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {relatedPosts.map((relPost) => (
                <Link
                  key={relPost.id}
                  to={`/blog/${relPost.slug}`}
                  className="group flex gap-6 items-start"
                >
                  <div className="w-32 h-24 flex-shrink-0 overflow-hidden rounded-sm">
                    <img
                      src={relPost.featured_image || 'https://images.unsplash.com/photo-1681018754447-abe57371b5de?crop=entropy&cs=srgb&fm=jpg&q=85'}
                      alt={relPost.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-stone-900 group-hover:text-red-800 transition-colors line-clamp-2">
                      {relPost.title}
                    </h3>
                    <p className="text-sm text-stone-500 mt-2">
                      {format(new Date(relPost.created_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default SinglePost;
